import { Embed } from './Embed';
import { TwitchPlayer } from './video/TwitchPlayer';
/**
 * A TS wrapper for the Twitch interactive media player, that can also include the chat.
 */
export class TwitchEmbed {
    /**
     * Creates a new TwitchEmbed instance.
     * @param divId The div HTML element where the player will appear.
     * @param options The player initialization options.
     */
    constructor(divId, options) {
        this._divId = divId;
        this._twitchEmbedOptions = options;
        try {
            if (window.Twitch && window.Twitch.Embed) {
                this._embed = new window.Twitch.Embed(divId, options);
            }
            else {
                console.warn('Player was created using the static file from inside the package. ' +
                    'Please add the Twitch.Embed script to *index.html*, if you want to download the script directly from Twitch');
                this._embed = new Embed(divId, options);
            }
            this._player = TwitchPlayer.FromPlayer(this._embed.getPlayer());
        }
        catch (e) {
            this._embed = new Embed(divId, options);
            this._player = TwitchPlayer.FromPlayer(this._embed.getPlayer());
            console.exception('Player was created using the static, packaged file. ' +
                'Please add the Twitch.Embed script to *index.html*, if you want to download the script directly from Twitch.', e);
        }
    }
    /**
     * The div HTML element where the player will appear.
     */
    get divId() {
        return this._divId;
    }
    /**
     * The player options that were provided when the instance was constructed.
     */
    get twitchEmbedOptions() {
        return this._twitchEmbedOptions;
    }
    /**
     * Adds an event listener for the given event.
     * @param event The event type to which the listener should react.
     * @param callback The logic that should happen when the listener fires.
     */
    addEventListener(event, callback) {
        var _a;
        (_a = this._embed) === null || _a === void 0 ? void 0 : _a.addEventListener(event.toString(), callback);
    }
    /**
     * Disables the captions for the content that is currently playing.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    disableCaptions() {
        this._embed.disableCaptions();
    }
    /**
     * Enables the captions for the content that is currently playing.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    enableCaptions() {
        this._embed.enableCaptions();
    }
    /**
     * Retrieves the playback statistics for this player.
     * The statistics contain information such as video FPS, resolution, latency and dropped frames.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    getPlaybackStatistics() {
        return this._embed.getPlaybackStats();
    }
    /**
     * Returns the channel’s identifier. Works only for live streams, not VODs.
     */
    getChannelId() {
        return this._embed.getChannelId();
    }
    /**
     * Returns the name of the collection currently being played.
     */
    getCollection() {
        return this._embed.getCollection();
    }
    /**
     * Sets the channel to be played.
     * @param channelId The selected channel's identifier.
     */
    setChannelId(channelId) {
        this._embed.setChannelId(channelId);
    }
    /**
     * Returns the Twitch.Player object.
     * Can be used for more fine-grained control of the player.
     */
    getPlayer() {
        return this._player;
    }
    /**
     * Pauses the player.
     */
    pause() {
        this._embed.pause();
    }
    /**
     * Begins playing the specified video.
     */
    play() {
        this._embed.play();
    }
    /**
     * Seeks to the specified timestamp (in seconds) in the video and resumes playing if paused. Does not work for live streams.
     * @param timestamp The specified timestamp (in seconds).
     */
    seek(timestamp) {
        this._embed.seek(timestamp);
    }
    /**
     * Sets the channel to be played.
     * @param channel The selected channel.
     */
    setChannel(channel) {
        this._embed.setChannel(channel);
    }
    /**
     * Sets the collection to be played.
     * Optionally also specifies the video within the collection, from which to start playback.
     * If a video ID is not provided here or the specified video is not part of the collection,
     * playback starts with the first video in the collection.
     * @param collectionId The identifier for the collection.
     * @param videoId The identifier for the video.
     */
    setCollection(collectionId, videoId) {
        this._embed.setCollection(collectionId, videoId);
    }
    /**
     * Sets the quality of the video. quality should be a string value returned by getQualities.
     * @param quality The quality to be set.
     */
    setQuality(quality) {
        this._embed.setQuality(quality);
    }
    /**
     * Sets the video to be played to be played and starts playback at timestamp (in seconds).
     * @param videoID The identifier of the video to be played.
     * @param timestamp The spot where the playback will be started (in seconds).
     */
    setVideo(videoID, timestamp) {
        this._embed.setVideo(videoID, timestamp);
    }
    /**
     * Returns true if the player is muted; otherwise, false.
     */
    getMuted() {
        return this._embed.getMuted();
    }
    /**
     * If true, mutes the player; otherwise, unmutes it. This is independent of the volume setting.
     * @param muted If true, player will be muted. Otherwise, it will be unmuted.
     */
    setMuted(muted) {
        this._embed.setMuted(muted);
    }
    /**
     * Returns the volume level, a value between 0.0 and 1.0.
     */
    getVolume() {
        return this._embed.getVolume();
    }
    /**
     * Sets the volume to the specified volume level, a value between 0.0 and 1.0.
     * @param volumeLevel A number between 0 and 1.
     */
    setVolume(volumeLevel) {
        this._embed.setVolume(volumeLevel);
    }
    /**
     * Returns the channel’s name. Works only for live streams, not VODs.
     */
    getChannel() {
        return this._embed.getChannel();
    }
    /**
     * Returns the current video’s timestamp, in seconds. Works only for VODs, not live streams.
     */
    getCurrentTime() {
        return this._embed.getCurrentTime();
    }
    /**
     * Returns the duration of the video, in seconds. Works only for VODs,not live streams.
     */
    getDuration() {
        return this._embed.getDuration();
    }
    /**
     * Returns true if the live stream or VOD has ended; otherwise, false.
     */
    getEnded() {
        return this._embed.getEnded();
    }
    /**
     * Returns the available video qualities. For example, chunked (pass-through of the original source).
     */
    getQualities() {
        return this._embed.getQualities();
    }
    /**
     * Returns the current quality of video playback.
     */
    getQuality() {
        return this._embed.getQuality();
    }
    /**
     * Returns the video ID. Works only for VODs, not live streams.
     */
    getVideo() {
        return this._embed.getVideo();
    }
    /**
     * Returns true if the video is paused; otherwise, false. Buffering or seeking is considered playing.
     */
    isPaused() {
        return this._embed.isPaused();
    }
}
//# sourceMappingURL=data:application/json;base64,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